import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  Tab,
  Tabs,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Public as MapIcon,
  Handshake as CollaborationIcon,
  TrendingUp as BusinessIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Settings as ConfigIcon,
  Euro as EuroIcon,
  Group as GroupIcon,
  Star as StarIcon,
  Download as DownloadIcon,
  Launch as LaunchIcon,
  Lightbulb as InnovationIcon,
  EmojiEvents as AwardIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';

const NetworkTab = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [partnersData, setPartnersData] = useState([]);
  const [collaborationsData, setCollaborationsData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDetailsDialog, setPartnerDetailsDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [geoData, setGeoData] = useState({ germany: null, luxembourg: null, cities: null });

  useEffect(() => {
    loadNetworkData();
  }, []);

  const loadNetworkData = async () => {
    try {
      // Load all partner hospital data
      const partnerIds = ['charite_berlin', 'hamburg_uke', 'munich_klinikum'];
      const partnerPromises = partnerIds.map(id => 
        fetch(`http://localhost:3002/api/network/partners/${id}`).then(res => res.json())
      );
      
      const [partners, collaborations, revenue, germany, luxembourg, cities] = await Promise.all([
        Promise.all(partnerPromises),
        fetch('http://localhost:3002/api/network/collaborations').then(res => res.json()),
        fetch('http://localhost:3002/api/network/revenue-projections').then(res => res.json()),
        fetch('http://localhost:3002/api/geographic/germany').then(res => res.json()),
        fetch('http://localhost:3002/api/geographic/luxembourg').then(res => res.json()),
        fetch('http://localhost:3002/api/geographic/cities').then(res => res.json())
      ]);

      setPartnersData(partners);
      setCollaborationsData(collaborations);
      setRevenueData(revenue);
      setGeoData({ germany, luxembourg, cities });
      setLoading(false);
    } catch (error) {
      console.error('Error loading network data:', error);
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'vision_zero_ready':
      case 'network_ready':
        return <CheckIcon color="success" />;
      case 'integration_pending':
      case 'onboarding':
        return <PendingIcon color="warning" />;
      default:
        return <ConfigIcon color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'vision_zero_ready':
      case 'network_ready':
        return 'success';
      case 'integration_pending':
      case 'onboarding':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getCountryFlag = (country) => {
    const flags = {
      'Germany': '🇩🇪',
      'Austria': '🇦🇹',
      'Switzerland': '🇨🇭',
      'Luxembourg': '🇱🇺'
    };
    return flags[country] || '🏥';
  };

  // Convert lat/lng to SVG coordinates
  const projectToSVG = (lat, lng, bounds, width, height) => {
    const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * width;
    const y = ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * height;
    return { x, y };
  };

  // Calculate bounds for the map
  const calculateBounds = () => {
    return {
      minLat: 47.2,
      maxLat: 55.1,
      minLng: 5.8,
      maxLng: 15.1
    };
  };

  // Convert GeoJSON coordinates to SVG path
  const coordsToPath = (coords, bounds, width, height) => {
    return coords.map((ring, ringIndex) => {
      const pathData = ring.map((point, pointIndex) => {
        const projected = projectToSVG(point[1], point[0], bounds, width, height);
        return `${pointIndex === 0 ? 'M' : 'L'} ${projected.x} ${projected.y}`;
      }).join(' ') + ' Z';
      return pathData;
    }).join(' ');
  };

  // Tab 1: Interactive Network Map
  const NetworkMapTab = () => {
    const bounds = calculateBounds();
    const mapWidth = 800;
    const mapHeight = 500;

    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon /> Vision Zero Network - Deutschland + Luxemburg
          </Typography>
          <Box>
            <Button variant="outlined" sx={{ mr: 1 }}>
              Focus: DACH Region
            </Button>
            <Button variant="contained" startIcon={<LocationIcon />}>
              🔄 Refresh
            </Button>
          </Box>
        </Box>

        {/* Real Geographic Map */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom align="center">
            🗺️ Deutschland & Luxemburg Partnernetzwerk
          </Typography>
          
          {geoData.germany && geoData.luxembourg && geoData.cities ? (
            <Box sx={{ 
              position: 'relative', 
              width: '100%', 
              height: 500,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#f8f9fa',
              borderRadius: 2,
              border: '1px solid #e9ecef'
            }}>
              <svg 
                width={mapWidth} 
                height={mapHeight} 
                viewBox={`0 0 ${mapWidth} ${mapHeight}`}
                style={{ width: '100%', height: '100%', maxWidth: '800px' }}
              >
                {/* Germany */}
                {geoData.germany.features.map((feature, index) => (
                  <path
                    key={`germany-${index}`}
                    d={coordsToPath(feature.geometry.coordinates, bounds, mapWidth, mapHeight)}
                    fill="rgba(25, 118, 210, 0.1)"
                    stroke="#1976d2"
                    strokeWidth="2"
                  />
                ))}
                
                {/* Luxembourg */}
                {geoData.luxembourg.features.map((feature, index) => (
                  <path
                    key={`luxembourg-${index}`}
                    d={coordsToPath(feature.geometry.coordinates, bounds, mapWidth, mapHeight)}
                    fill="rgba(76, 175, 80, 0.2)"
                    stroke="#4caf50"
                    strokeWidth="2"
                  />
                ))}

                {/* Cities and Partners */}
                {geoData.cities.cities.map((city) => {
                  const projected = projectToSVG(city.lat, city.lng, bounds, mapWidth, mapHeight);
                  const partner = partnersData.find(p => 
                    p.hospital_info.location.address.toLowerCase().includes(city.name.toLowerCase())
                  );
                  
                  return (
                    <g key={city.name}>
                      {/* City marker */}
                      <circle
                        cx={projected.x}
                        cy={projected.y}
                        r={partner ? 12 : 6}
                        fill={partner ? 
                          (partner.hospital_info.network_status === 'vision_zero_ready' ? '#4caf50' : '#ff9800') : 
                          '#666'
                        }
                        stroke="white"
                        strokeWidth="3"
                        style={{ cursor: partner ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (partner) {
                            setSelectedPartner(partner);
                            setPartnerDetailsDialog(true);
                          }
                        }}
                      />
                      
                      {/* City label */}
                      <text
                        x={projected.x}
                        y={projected.y - (partner ? 20 : 15)}
                        textAnchor="middle"
                        fontSize={partner ? "14" : "12"}
                        fontWeight={partner ? "600" : "400"}
                        fill={partner ? "#1976d2" : "#666"}
                      >
                        {city.name === 'München' ? 'München' : city.name}
                      </text>
                      
                      {/* Partner info */}
                      {partner && (
                        <text
                          x={projected.x}
                          y={projected.y + 25}
                          textAnchor="middle"
                          fontSize="10"
                          fill="#4caf50"
                          fontWeight="500"
                        >
                          {Math.round(partner.capabilities.technical_readiness.gold_compatibility_score * 100)}% GOLD
                        </text>
                      )}
                      
                      {/* Pulsing animation for ready partners */}
                      {partner && partner.hospital_info.network_status === 'vision_zero_ready' && (
                        <circle
                          cx={projected.x}
                          cy={projected.y}
                          r="15"
                          fill="none"
                          stroke="#4caf50"
                          strokeWidth="2"
                          opacity="0"
                        >
                          <animate
                            attributeName="r"
                            values="12;20;12"
                            dur="2s"
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.7;0;0.7"
                            dur="2s"
                            repeatCount="indefinite"
                          />
                        </circle>
                      )}
                    </g>
                  );
                })}

                {/* Network Connection Lines */}
                {partnersData.length > 1 && (
                  <g>
                    <defs>
                      <linearGradient id="networkGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(76, 175, 80, 0.4)" />
                        <stop offset="100%" stopColor="rgba(33, 150, 243, 0.4)" />
                      </linearGradient>
                    </defs>
                    
                    {/* Connect Berlin and Hamburg */}
                    {(() => {
                      const berlin = geoData.cities.cities.find(c => c.name === 'Berlin');
                      const hamburg = geoData.cities.cities.find(c => c.name === 'Hamburg');
                      if (berlin && hamburg) {
                        const berlinProj = projectToSVG(berlin.lat, berlin.lng, bounds, mapWidth, mapHeight);
                        const hamburgProj = projectToSVG(hamburg.lat, hamburg.lng, bounds, mapWidth, mapHeight);
                        return (
                          <line
                            x1={berlinProj.x} y1={berlinProj.y}
                            x2={hamburgProj.x} y2={hamburgProj.y}
                            stroke="url(#networkGradient)"
                            strokeWidth="3"
                            strokeDasharray="8,4"
                          />
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Connect Berlin and München */}
                    {(() => {
                      const berlin = geoData.cities.cities.find(c => c.name === 'Berlin');
                      const munich = geoData.cities.cities.find(c => c.name === 'München');
                      if (berlin && munich) {
                        const berlinProj = projectToSVG(berlin.lat, berlin.lng, bounds, mapWidth, mapHeight);
                        const munichProj = projectToSVG(munich.lat, munich.lng, bounds, mapWidth, mapHeight);
                        return (
                          <line
                            x1={berlinProj.x} y1={berlinProj.y}
                            x2={munichProj.x} y2={munichProj.y}
                            stroke="url(#networkGradient)"
                            strokeWidth="3"
                            strokeDasharray="8,4"
                          />
                        );
                      }
                      return null;
                    })()}
                  </g>
                )}
                
                {/* Country Labels */}
                <text x="400" y="50" textAnchor="middle" fontSize="24" fontWeight="300" fill="rgba(0,0,0,0.3)">
                  🇩🇪 Deutschland
                </text>
                <text x="150" y="200" textAnchor="middle" fontSize="18" fontWeight="300" fill="rgba(0,0,0,0.3)">
                  🇱🇺 Luxemburg
                </text>
              </svg>

              {/* Map Legend */}
              <Paper sx={{ 
                position: 'absolute', 
                top: 15, 
                right: 15,
                p: 2,
                backgroundColor: 'rgba(255,255,255,0.95)',
                boxShadow: 3
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Partner Status:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
                    <Typography variant="caption">Vision Zero Ready</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
                    <Typography variant="caption">Integration Pending</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#666' }} />
                    <Typography variant="caption">Andere Städte</Typography>
                  </Box>
                </Box>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  Christof Verbindung:
                </Typography>
                <Typography variant="caption" color="success.main">
                  🇱🇺 Luxembourg Institute of Health
                </Typography>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          )}
        </Card>

        {/* Partner Cards Row */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
          🏥 ARGUS Partner Details:
        </Typography>
        <Grid container spacing={2}>
          {partnersData.map((partner) => (
            <Grid item xs={12} sm={6} md={4} key={partner.hospital_info.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  height: '100%',
                  '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  setSelectedPartner(partner);
                  setPartnerDetailsDialog(true);
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ mr: 1 }}>
                      {getCountryFlag(partner.hospital_info.country)}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {partner.hospital_info.location.address.split(',')[1]?.trim() || partner.hospital_info.name.split(' ')[0]}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {partner.hospital_info.name.length > 30 ? 
                      partner.hospital_info.name.substring(0, 30) + '...' : 
                      partner.hospital_info.name
                    }
                  </Typography>
                  
                  <Chip 
                    label={partner.hospital_info.network_status.replace(/_/g, ' ').replace('zero', 'Zero')} 
                    size="small" 
                    color={getStatusColor(partner.hospital_info.network_status)}
                    sx={{ mb: 1 }}
                  />
                  
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                    {Math.round(partner.capabilities.technical_readiness.gold_compatibility_score * 100)}% GOLD Compatible
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    {(partner.hospital_info.annual_patients / 1000).toFixed(0)}k patients/year
                  </Typography>
                  
                  <Typography variant="body2" color="primary.main" sx={{ mt: 1, fontWeight: 500 }}>
                    Revenue Potential: €{(partner.revenue_potential.network_enhanced_projection / 1000).toFixed(0)}k
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Network Summary */}
        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 3, bgcolor: 'primary.light', color: 'white' }}>
            <Typography variant="h6" gutterBottom>📊 Deutschland Network Summary</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)', color: 'primary.main' }}>
                  <Typography variant="h4">{partnersData.length}</Typography>
                  <Typography variant="body2">DACH Partner</Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)', color: 'success.main' }}>
                  <Typography variant="h4">{partnersData.filter(p => p.hospital_info.network_status === 'vision_zero_ready').length}</Typography>
                  <Typography variant="body2">Vision Zero Ready</Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)', color: 'info.main' }}>
                  <Typography variant="h4">
                    {Math.round(partnersData.reduce((sum, p) => sum + p.hospital_info.annual_patients, 0) / 1000)}k
                  </Typography>
                  <Typography variant="body2">Patienten/Jahr</Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)', color: 'secondary.main' }}>
                  <Typography variant="h4">
                    €{Math.round(partnersData.reduce((sum, p) => sum + p.revenue_potential.network_enhanced_projection, 0) / 1000000 * 10) / 10}M
                  </Typography>
                  <Typography variant="body2">Revenue Potential</Typography>
                </Paper>
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}>
              <strong>Christof Connection:</strong> 🇱🇺 Luxembourg Institute of Health bietet ideale Erweiterung für EU-weites Netzwerk.
              Potentielle Integration würde weitere €400k Revenue und Zugang zu EU-Forschungsförderung ermöglichen.
            </Alert>
            
            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              <Button variant="contained" sx={{ bgcolor: 'white', color: 'primary.main' }} startIcon={<LocationIcon />}>
                Partner Details
              </Button>
              <Button variant="outlined" sx={{ borderColor: 'white', color: 'white' }} startIcon={<CollaborationIcon />}>
                Plan Multi-Site Study
              </Button>
              <Button variant="outlined" sx={{ borderColor: 'white', color: 'white' }} startIcon={<DownloadIcon />}>
                Download Report
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  };

  // Tab 2: Collaboration Opportunities
  const CollaborationTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CollaborationIcon /> Collaborative Research Opportunities
        </Typography>
        <Box>
          <Button variant="outlined" sx={{ mr: 1 }}>
            Sort by: Revenue Potential
          </Button>
          <Button variant="outlined">
            Filter: Ready to Start
          </Button>
        </Box>
      </Box>

      <Typography variant="h6" gutterBottom sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        🔥 High Priority Opportunities:
      </Typography>

      {collaborationsData?.multi_site_opportunities?.map((opportunity) => (
        <Card key={opportunity.opportunity_id} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {opportunity.opportunity_id === 'onco_car_t_2024' ? '🎗️' : 
                   opportunity.opportunity_id === 'cardio_toxicity_2024' ? '🫀' : '🧬'} 
                  {opportunity.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {opportunity.description}
                </Typography>
              </Box>
              <Chip 
                label={`€${opportunity.estimated_revenue_per_site.toLocaleString()}`} 
                color="primary" 
                variant="outlined"
              />
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Partnership:</strong> {opportunity.matching_partners.map(p => p.hospital_name.split(' ')[0]).join(' + ')} + Your Hospital
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Patient Volume:</strong> {opportunity.matching_partners.reduce((sum, p) => sum + p.patient_volume, 0).toLocaleString()} combined
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Duration:</strong> {opportunity.study_duration_months} months
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Your Match Score:</strong> 
                  <Chip 
                    label={`${Math.round(opportunity.matching_partners[0]?.match_score * 100 || 96)}% ⭐`} 
                    size="small" 
                    color="success" 
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid>
            </Grid>

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Required Capabilities:</strong>
            </Typography>
            <Box sx={{ mb: 2 }}>
              {opportunity.required_specialization.map((spec) => (
                <Chip key={spec} label={`✅ ${spec}`} size="small" sx={{ mr: 0.5 }} />
              ))}
              {opportunity.vision_zero_aligned && (
                <Chip label="✅ Vision Zero Aligned" size="small" color="success" sx={{ mr: 0.5 }} />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<LaunchIcon />}>
                Start Partnership Discussion
              </Button>
              <Button variant="outlined">
                View Detailed Requirements
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Card sx={{ mt: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            💡 Smart Matchmaking Suggestions:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• München's AI parameter mapping would boost your efficiency 15%" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Hamburg's data completeness score (96%) complements your biomarker expertise" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Wien's advanced imaging protocols align with your research focus" />
            </ListItem>
          </List>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" sx={{ mr: 1 }}>
              Create Custom Partnership
            </Button>
            <Button variant="outlined">
              Network Intelligence Report
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  // Tab 3: Business Impact Dashboard
  const BusinessImpactTab = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon /> Vision Zero Network - Business Impact
        </Typography>
        <Box>
          <Button variant="outlined" sx={{ mr: 1 }}>
            Time Period: Year 1
          </Button>
          <Button variant="outlined">
            View: Revenue Focus
          </Button>
        </Box>
      </Box>

      {/* Revenue Transformation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            💰 Revenue Transformation:
          </Typography>
          
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={5}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.100' }}>
                <Typography variant="h6" color="text.secondary">Current (Solo)</Typography>
                <Typography variant="h3" sx={{ my: 2, fontWeight: 700 }}>€300,000/year</Typography>
                <Box sx={{ bgcolor: 'grey.300', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2">Basic Reports</Typography>
                  <Typography variant="body2">€10k average</Typography>
                  <Typography variant="body2">30 reports</Typography>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h4" color="primary">→</Typography>
            </Grid>
            
            <Grid item xs={5}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
                <Typography variant="h6">Network (Vision Zero)</Typography>
                <Typography variant="h3" sx={{ my: 2, fontWeight: 700 }}>€1,200,000/year</Typography>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2">Enhanced Reports</Typography>
                  <Typography variant="body2">€42k average</Typography>
                  <Typography variant="body2">50 reports + Network</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="h6">4x Revenue Multiplier through Vision Zero Partnership</Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Network Value Drivers */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🚀 Network Value Drivers:
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Value Driver</strong></TableCell>
                  <TableCell><strong>Individual</strong></TableCell>
                  <TableCell><strong>Network Effect</strong></TableCell>
                  <TableCell><strong>Multiplier</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Report Premium</TableCell>
                  <TableCell>€10,000</TableCell>
                  <TableCell>€42,000</TableCell>
                  <TableCell><Chip label="4.2x" color="success" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Quality Bonus</TableCell>
                  <TableCell>Standard</TableCell>
                  <TableCell>+€8,000</TableCell>
                  <TableCell><Chip label="+25%" color="info" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Multi-Site Studies</TableCell>
                  <TableCell>N/A</TableCell>
                  <TableCell>€180,000</TableCell>
                  <TableCell><Chip label="New Stream" color="primary" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Network Intelligence</TableCell>
                  <TableCell>N/A</TableCell>
                  <TableCell>€120,000</TableCell>
                  <TableCell><Chip label="New Stream" color="primary" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Consortium Benefits</TableCell>
                  <TableCell>N/A</TableCell>
                  <TableCell>€400,000</TableCell>
                  <TableCell><Chip label="Membership" color="secondary" /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ROI Timeline */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            ⏱️ ROI Timeline:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText 
                primary="Month 1-3: Setup & Integration" 
                secondary="Investment: €25,000"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Month 4: Break-even achieved" 
                secondary="Revenue: €85,000"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Month 6: First multi-site study" 
                secondary="Profit: €180,000"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Year 1: Full network benefits" 
                secondary="Total: €1,200,000"
              />
            </ListItem>
          </List>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>ROI Progress:</Typography>
            <LinearProgress 
              variant="determinate" 
              value={100} 
              sx={{ height: 8, borderRadius: 4 }}
              color="success"
            />
            <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 600 }}>
              📊 320% ROI in Year 1
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Strategic Advantages */}
      <Paper sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🎯 Strategic Advantages:
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon><AwardIcon sx={{ color: 'primary.contrastText' }} /></ListItemIcon>
            <ListItemText primary="GOLD Standard Certification (€500k value)" />
          </ListItem>
          <ListItem>
            <ListItemIcon><MapIcon sx={{ color: 'primary.contrastText' }} /></ListItemIcon>
            <ListItemText primary="European Market Access (5 countries)" />
          </ListItem>
          <ListItem>
            <ListItemIcon><InnovationIcon sx={{ color: 'primary.contrastText' }} /></ListItemIcon>
            <ListItemText primary="AI-Enhanced Data Quality (+15% premium)" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon sx={{ color: 'primary.contrastText' }} /></ListItemIcon>
            <ListItemText primary="Regulatory Pre-Approval (faster time-to-market)" />
          </ListItem>
        </List>
        
        <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<DownloadIcon />} sx={{ bgcolor: 'white', color: 'primary.main' }}>
            Download Business Case
          </Button>
          <Button variant="outlined" sx={{ borderColor: 'white', color: 'white' }}>
            Schedule Partnership Call
          </Button>
          <Button variant="outlined" sx={{ borderColor: 'white', color: 'white' }}>
            Start Trial
          </Button>
        </Box>
      </Paper>
    </Box>
  );

  // Partner Details Dialog
  const PartnerDetailsDialog = () => (
    <Dialog open={partnerDetailsDialog} onClose={() => setPartnerDetailsDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {selectedPartner && getCountryFlag(selectedPartner.hospital_info.country)} 
        {selectedPartner?.hospital_info.name}
      </DialogTitle>
      <DialogContent>
        {selectedPartner && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Annual Patients:</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {selectedPartner.hospital_info.annual_patients.toLocaleString()}
              </Typography>
              
              <Typography variant="subtitle2">GOLD Compatibility:</Typography>
              <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                {Math.round(selectedPartner.capabilities.technical_readiness.gold_compatibility_score * 100)}%
              </Typography>
              
              <Typography variant="subtitle2">Network Status:</Typography>
              <Chip 
                label={selectedPartner.hospital_info.network_status.replace(/_/g, ' ')} 
                color={getStatusColor(selectedPartner.hospital_info.network_status)} 
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Specializations:</Typography>
              {selectedPartner.hospital_info.specializations.map((spec) => (
                <Chip key={spec} label={spec} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
              ))}
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Revenue Potential:</Typography>
              <Typography variant="body2" color="success.main">
                €{selectedPartner.revenue_potential.network_enhanced_projection.toLocaleString()}/year
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPartnerDetailsDialog(false)}>Close</Button>
        <Button variant="contained" startIcon={<CollaborationIcon />}>
          Start Collaboration
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <LinearProgress sx={{ width: '50%' }} />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon /> Vision Zero Network
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Transform your research potential through the European Vision Zero Consortium
          </Typography>
        </Box>

        {/* Network Overview Alert */}
        <Alert severity="success" sx={{ mb: 3 }}>
          <strong>Network Ready:</strong> 5 ARGUS partner hospitals across 4 countries with 456,000 patients/year. 
          Average GOLD compatibility: 94.2%. Network revenue potential: €6.3M/year.
        </Alert>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="network tabs">
            <Tab 
              icon={<MapIcon />} 
              label="Interactive Map" 
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<CollaborationIcon />} 
              label="Collaboration Opportunities" 
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<BusinessIcon />} 
              label="Business Impact Dashboard" 
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ mt: 3 }}>
          {currentTab === 0 && <NetworkMapTab />}
          {currentTab === 1 && <CollaborationTab />}
          {currentTab === 2 && <BusinessImpactTab />}
        </Box>

        <PartnerDetailsDialog />
      </Box>
    </MainLayout>
  );
};

export default NetworkTab;